// AppShellHost — the composition root and the ONE impure shell.
//
// This is the only stateful/impure file in the mock app: it holds the frame
// state, calls the `AppPorts` seam inside `useEffect` (the sanctioned impurity
// boundary — fetch-shaped calls + `useState`, not a buried `void` smell), and
// folds every reported intent through the pure mock folds. Every component it
// renders stays pure and receives its data via props.
//
// Frame state lives in ONE `FrameState` product held by a single
// `useReducer(foldFrame, …)` — the pure frame decider (app/frame.ts) is the
// ONLY writer of frame factors (composition contract I2): handlers dispatch
// named `FrameTransition` values instead of setting factors imperatively, so
// a transition that moves an unnamed region cannot exist. The factors:
//   openSecondary — which secondary is summoned, or null for Focus (closed)
//   nav      — the rail's display state
//   paneTree — the author's working memory (TabSet, whole-workspace now that
//              Project no longer sits between workspace and resource — the
//              active tab routes the centre)
//   overlay — the one transient surface floated over everything (Overlay)
//   notice  — the toast currently showing
// plus anchor, the closed-tab + recency memories and the
// blocked-close confirmation. Outside the frame: the per-material compose
// wear (loop, folded via composeLoop.ts), the loaded data (the workspace
// library / elements / conversation / the active source or package view
// detail) and a drafting flag.
//
// Object-nav wiring notes (deliberate MVP shortcuts, each commented in place):
//   • Tabs are never dirty here — every edit commits transactionally through
//     the update primitives (optimistic + rollback), so there is no unsaved
//     buffer to guard. The dirty/discard machinery stays wired (the folds and
//     ConfirmDiscard render when a dirty tab exists) for the day a buffered
//     editor arrives.
//   • The chat context chip is PORT-HONEST: `ports.conversationScope` decides
//     the displayed scope and the switchable set, so the chip never claims a
//     projection the backend doesn't serve (the real server's conversations
//     are workspace-global; the mock's are per-material).

import { Result } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import { useMemo } from "react";
import type { SelectableContext } from "../../components/Chat/types.js";
import { useHeroHost } from "../../components/HeroHost/HeroHost.js";
import type { NewMaterial } from "../../components/MaterialCreate/MaterialCreate.js";
import { resourceDisplayTitle } from "../../components/Navigation/displayTitle.js";
import type { ConversationId } from "../../components/Navigation/types.js";
import { libraryToSearchResults } from "../../components/Search/searchProjection.js";
import { useNow } from "../../hooks/useNow.js";
import { mkChatIntents } from "../../modules/chat.js";
import type { IngestId } from "../ingest.js";
import { activeProjectOf, routeOfFrame } from "../route/bridge.js";
import { restoreProjectNow } from "./chrome/useProjectItems.js";
import { toComposerResourceRef } from "./constants.js";
import type { AppShellModel } from "./context.js";
import { shellNavIntents } from "./shellNavIntents.js";
import { useChatSignalRoutes } from "./useChatModule.js";
import { useConnectionHealth } from "./useConnectionHealth.js";
import { useContextSpine } from "./useContextSpine.js";
import { useExportFormats } from "./useExportFormats.js";
import { useLibraryLoad } from "./useLibraryLoad.js";
import { useLibraryMount } from "./useLibraryModule.js";
import { useLifecycleSurfaces } from "./useLifecycleSurfaces.js";
import { useMaterialSideView } from "./useMaterialSideView.js";
import type { NoticeViewDeps } from "./useNoticeView.js";
import { useObjectNav } from "./useObjectNav.js";
import { useSearchChord } from "./useSearchChord.js";
import { useShellFoundation } from "./useShellFoundation.js";
import { useStopKey } from "./useStopKey.js";

// Mint a fresh conversation id client-side for an explicit new-conversation
// gesture (⌘T / "+" / source→chat). The tab carries this id from the moment it
// opens and keeps it once the first send makes it durable (no id swap — hero-is-
// initial-conversation-state §3.1). `crypto.randomUUID` is the same minter the
// intent recorder already uses at the shell edge.
const mkConversationId = (): ConversationId => globalThis.crypto.randomUUID();

// useAppShellHost — the impure shell as a hook. Owns ALL frame/chat/data state,
// the ~12 host sub-hooks, every load effect, and every intent handler, and
// assembles the `AppShellModel` the pure view tree projects. Keeping it a hook
// (not a component) lets AppShellHost stay a thin composition root: it calls
// this once, gates on the boot facts, and renders the provider + views.
export const useAppShellHost = (): AppShellModel => {
    const {
        ports,
        displayText,
        turnFailureText,
        kbdPlatform,
        routeNotFound,
        onRouteHome,
        frame,
        dispatch,
        overlay,
        paneTree,
        createSeedTitle,
        setCreateSeedTitle,
        historyView,
        setHistoryView,
        fileHistory,
        setFileHistory,
        historyVersionId,
        setHistoryVersionId,
        finderDocs,
        setFinderDocs,
        finderLoading,
        setFinderLoading,
        renaming,
        setRenaming,
        confirmDelete,
        setConfirmDelete,
        confirmDeleteConversation,
        setConfirmDeleteConversation,
        displayName,
        panelLayout,
        resizeRail,
        resizeDock,
        resetRail,
        resetDock,
        dockDragPull,
        setDockDragPull,
        railDragPull,
        setRailDragPull,
        dockDragCfg,
        railDragCfg,
        endDockDrag,
        endRailDrag,
        onDisplayNameChange,
        workspaceName,
        onWorkspaceNameChange,
        summaryConsent,
        onSummaryConsentChange: onSummaryConsentChangeRaw,
        library,
        setLibrary,
        libraryError,
        setLibraryError,
        authStatus,
        setAuthStatus,
        bootHealthSigRef,
        loadedMaterialRef,
        sourceDetail,
        setSourceDetail,
        sourceError,
        setSourceError,
        sourceReloadKey,
        setSourceReloadKey,
        exportDetail,
        setExportDetail,
        exportError,
        setExportError,
        chatState,
        sendChat,
        chatRuntime,
        selectedConversationId,
        sessionFor,
        conversationSummaries,
        conversationList,
        setConversationList,
        conversationListSeq,
        retryConversationList,
        updateConversationSummaries,
        noteConversationStarted,
        conversationIsDurable,
        conversationTranscriptPending,
        learningContexts,
        setLearningContexts,
        quality,
        setQuality,
        policy,
        setPolicy,
        policyCache,
        setPolicyCache,
        paneSubject,
        setPaneSubject,
        pendingProposalsByMaterialRef,
        tipIndex,
        tipsDismissed,
        setTipsDismissed,
        activeContextItems,
        setActiveContextItems,
        activeAmbientItems,
        setActiveAmbientItems,
        exportState,
        setExportState,
        exportReady,
        setExportReady,
        activeMaterialTemplate,
        setActiveMaterialTemplate,
        templateCatalog,
        setTemplateCatalog,
        createState,
        setCreateState,
        ingest,
        setIngest,
        ingestConversations,
        setIngestConversations,
        ingestHostRef,
        openFilePicker,
        addPastedText,
        materialRefs,
        sourceItems,
        refFor,
        resolveAmbientItems,
        composerCandidates,
        chatSlashCommands,
        navHost,
        revealRail,
        isSplit,
        tabs,
        activeRef,
        activeMaterialRef,
        activeMaterialId,
        activeConversationId,
        visibleConversationIds,
        visibleConversationIdsKey,
        drafting,
        activeSourceId,
        activeExportId,
        activeQualityMaterialId,
        activeContextAnchor,
        activeContextAnchorKey,
        dock,
        surface,
        scope,
        summonSlot,
        showNotice,
        messageControls,
        openOverlay,
        closeOverlay,
        coach,
        editor,
        studio,
    } = useShellFoundation();
    // The composer's state lives in the chat Model (S4); written only via
    // chat commands (`sendChat`).
    const { composerMentions, pendingGrounding } = chatState;

    // === Connection health (offline round Stage A) ===========================
    // The offline-detection + backoff-retry state machine: a read outcome is
    // folded into the standing `health` fact (transient → offline + backoff
    // retry; ok → online), `retrySeq` is the re-read signal the load effects
    // subscribe to, `retryNow` bypasses the backoff for author agency. A
    // terminal error is NOT a connectivity fact — it flows to `libraryError`.
    const {
        health,
        retrySeq,
        setRetrySeq,
        lastCheckedAt,
        reason: healthReason,
        nextRetryAt,
        foldReadHealth,
        isTransient,
        retryNow,
    } = useConnectionHealth();

    // Sign out: the port ends the session and leaves the page. The one place
    // the app signs out (the menu and Settings both ask here).
    const signOut = ports.signOut;

    // Wrap the raw consent dispatch (`useShellFoundation`) with a forced
    // reread once the write settles: the server projection
    // (`library.summary.consentEnabled`) is the source of truth, so after a
    // toggle the client re-fetches `/state` to pick up the server-confirmed
    // value (and any summary the change unblocks) rather than trusting the
    // optimistic mirror indefinitely. Fire-and-forget from the caller's
    // perspective — `onSummaryConsentChangeRaw` already reports its own
    // failure notice; this only adds the reread on top.
    const onSummaryConsentChange = (consented: boolean): void => {
        void onSummaryConsentChangeRaw(consented).then(() =>
            setRetrySeq((s) => s + 1),
        );
    };

    // === Load effects (the sanctioned port calls in useEffect) ===============
    // The ~20 world-load port-call effects live
    // in a dedicated effect-bundle hook (C1 #9). Called HERE — right after
    // useConnectionHealth, before the object-nav hub — so every effect keeps its
    // declaration order relative to the sibling hooks; the effect bodies + dep
    // arrays are unchanged.
    useLibraryLoad({
        ports,
        setAuthStatus,
        setTemplateCatalog,
        setLearningContexts,
        overlay,
        setFileHistory,
        setHistoryVersionId,
        setHistoryView,
        setFinderDocs,
        setFinderLoading,
        setLibraryError,
        foldReadHealth,
        setLibrary,
        dispatch,
        showNotice,
        isTransient,
        retrySeq,
        bootHealthSigRef,
        mkConversationId,
        health,
        authStatus,
        paneTree,
        studioProject: frame.studioProject,
        activeSourceId,
        sourceReloadKey,
        setSourceError,
        setSourceDetail,
        activeExportId,
        setExportDetail,
        setExportError,
        activeContextAnchor,
        activeContextAnchorKey,
        library,
        setActiveContextItems,
        refFor,
        setActiveAmbientItems,
        resolveAmbientItems,
        activeConversationId,
        setConversationList,
        conversationListSeq,
        visibleConversationIds,
        visibleConversationIdsKey,
        sendChat,
        activeMaterialId,
        activeMaterialRef,
        loadedMaterialRef,
        editor,
        setQuality,
        setPolicy,
        setActiveMaterialTemplate,
        setPaneSubject,
        policy,
        activeQualityMaterialId,
        setPolicyCache,
    });

    // === Object-nav routing hub (the frame fold advances the state) ========
    // The thin object-nav handlers, the ONE widened trigger dispatcher
    // (`dispatchTrigger` — determinism M2) and its two stamped doors
    // (`applyTrigger` + the ingest twin `applyIngest`) now live in a
    // render-local factory (C1 #8). It calls no React hooks, so this call site
    // adds nothing to the component's hook order. The frame state stays owned
    // here (the `useReducer` above is read at first paint + in earlier
    // effects); the hub receives `dispatch`/`paneTree` and returns the stable
    // API the rest of the shell consumes. Forward-declared handlers (the
    // command palette, settings nav, create dialog, ingest bridge) are
    // injected as deferred arrows — each fires only on a later event.
    const {
        openConversation,
        enterFullChat,
        openRef,
        onActivateTab,
        onFinderJump,
        goToChange,
        onCloseTab,
        applyTrigger,
        applyIngest,
    } = useObjectNav({
        dispatch,
        paneTree,
        selectedConversationId,
        mkConversationId,
        ports,
        refFor,
        openOverlay,
        openFilePicker,
        openSearch: () => summonSearch(),
        gotoSettings: () => gotoSettings(),
        openCreateDialog: () => openCreateDialog(),
        sendChat,
        raiseSignal: chatRuntime.emit,
        applyIngestToHost: (command) =>
            ingestHostRef.current?.applyCommand(command),
    });

    // The ONE keyboard chord the web build registers: ⌘K opens Search
    // (CLAUDE.md; IDD 73062339) via the ONE stamped `applyTrigger` door.
    useSearchChord({ applyTrigger, kbdPlatform });

    const {
        navBack,
        navForward,
        gotoSettings,
        closeSettings,
        gotoHome,
        closeHome,
        gotoStudio,
        gotoKnowledge,
        gotoSearch,
        closeSearch,
        startNewConversation,
    } = shellNavIntents(
        dispatch,
        mkConversationId,
        () =>
            !conversationIsDurable(activeConversationId) &&
            sessionFor(activeConversationId).conversation.length === 0,
    );

    // Open the material-creation dialog with a fresh submission state — the ONE
    // entry the rail's "+", the File-menu accelerator and the empty library
    // share. The block palette is NOT reachable here (creation ≠ insertion).
    const openCreateDialog = (seedTitle = "") => {
        setCreateSeedTitle(seedTitle);
        setCreateState({ type: "idle" });
        openOverlay({ type: "create" });
    };

    // Create a material from the dialog's pick: run `createMaterial`, refresh
    // the library, land the author in the editor. A refusal keeps the dialog
    // open with the typed reason.
    const onCreateMaterial = (material: NewMaterial) => {
        setCreateState({ type: "creating" });
        const run = async () => {
            const r = await ports.createMaterial(material);
            if (Result.isErr(r)) {
                setCreateState({
                    type: "failed",
                    message: displayText(r.error),
                });
                return;
            }
            const lib = await ports.loadLibrary();
            if (Result.isOk(lib)) setLibrary(lib.value);
            openRef({
                type: "material",
                id: r.value.materialId,
                title: resourceDisplayTitle(
                    material.title,
                    "material",
                    t`Untitled`,
                ),
            });
            setCreateState({ type: "idle" });
            closeOverlay();
        };
        void run();
    };

    // Situation-profile grounding ("who this is for") is a per-conversation
    // durable write pair. It lives INSIDE the ContextSideCard's render (the
    // local `ground`/`unground`, scoped to that card's conversation id), now
    // that the corner float's banner chip — the former top-level consumer — is
    // gone.

    // === Context (spine step 3a — the always-visible EXPLICIT context strip) ===
    // The Context BC write handlers (introduce / drop / clear + their nascent
    // twins + the composer @-mention bridge), lifted into a render-local factory
    // (C1). It calls no React hooks, so this call site adds nothing to the hook
    // order; it threads the shell state + setters the handlers read/write. Each
    // durable write awaits its port then refetches BOTH tiers (explicit +
    // ambient) so the always-visible strip reflects the applied change.
    const {
        reloadActiveContext,
        introduceIntoActiveContext,
        groundAddedSource,
        promoteAmbientItem,
        dropFromActiveContext,
        clearActiveContext,
        handleMentionsChange,
    } = useContextSpine({
        ports,
        refFor,
        resolveAmbientItems,
        conversationIsDurable,
        activeContextAnchor,
        activeConversationId,
        composerMentions,
        sendChat,
        setActiveContextItems,
        setActiveAmbientItems,
    });

    // === Lifecycle surfaces (master audit R5) ================================
    // The shared rename / delete+undo / duplicate flows, the source
    // summary/notes commands, the export packaging + citation-gate flow, the
    // overflow-menu builders now live in a render-local hook (C1 #9). It calls
    // one stable hook (useLingui, for the menu-label macros — the CoachHost
    // precedent); the forward-declared `openHistoryForResource` (History
    // overlay) is injected as a deferred arrow.
    const {
        submitResourceRename,
        conversationTitleFor,
        submitConversationRename,
        deleteResourceNow,
        undoDelete,
        archiveResourceNow,
        unarchiveResourceNow,
        promoteFileNow,
        deleteConversationNow,
        undoDeleteConversation,
        saveSourceSummaryNow,
        addSourceNote,
        deleteSourceNote,
        requestUploadForSource,
        downloadExportById,
        resourceOverflowItems,
        beginRenameConversation,
        archiveConversationNow,
        unarchiveConversationNow,
        retrySave,
        packageNow,
        onExportStart,
    } = useLifecycleSurfaces({
        ports,
        setLibrary,
        dispatch,
        showNotice,
        displayText,
        selectCategory: navHost.selectCategory,
        setRenaming,
        conversationSummaries,
        updateConversationSummaries,
        retryConversationList,
        setConfirmDelete,
        setConfirmDeleteConversation,
        setSourceReloadKey,
        setExportState,
        setExportReady,
        mkConversationId,
        refFor,
        openRef,
        activeSourceId,
        activeMaterialId,
        exportMaterialId:
            frame.overlay.type === "export" ? frame.overlay.materialId : null,
        quality,
        policy,
        openHistoryForResource: (resourceKey) =>
            openHistoryForResource(resourceKey),
    });

    // The generation side view (material module, phase 2c), in the shell's
    // runtime (it hears a turn's WorldChanged; its Signals ride the chat route
    // table below); it follows the right panel's focus.
    const materialSide = useMaterialSideView({
        runtime: chatRuntime,
        ports,
        frame,
    });

    // === Chat (module mounted in the foundation, S6) =========================
    // The route table for chat's Signals, the intents (each a UI gesture → one
    // chat command), and Escape-while-drafting → stop.
    // The Library module, in the same runtime (its frame requests ride the
    // chat route table above).
    const libraryMount = useLibraryMount(chatRuntime, ports);
    // Search, its field focused: ⌘K and the menu's Search item (owner, 3 Nov;
    // IDD 73062339). On the Search page already: its tab and field stay (an
    // item gone into goes back to the list); elsewhere it opens on Sources.
    const summonSearch = (): void => {
        const at = tabs.active.type === "search" ? tabs.active : undefined;
        dispatch({ type: "open-search", kind: at?.kind, query: at?.query });
        libraryMount.send({ type: "search_summoned" });
    };
    useChatSignalRoutes(chatRuntime, {
        showNotice,
        dispatch,
        setRetrySeq,
        noteConversationStarted,
        reloadActiveContext,
        editor,
        pendingProposalsByMaterialRef,
    });
    const {
        submitText,
        retryTurn,
        stopTurn,
        acceptOpeningMove,
        draftMaterialFromSource,
        supplyFormAnswer,
    } = mkChatIntents({
        sendChat,
        chatState,
        conversationIsDurable,
        ingest,
        ingestConversations,
        activeMaterialId,
        materialRefs,
        activeContextAnchor,
        activeProject: activeProjectOf(routeOfFrame(frame)),
        // Only while the Studio is the view the composer sends from.
        activeLocation:
            tabs.active.type === "studio" ? (studio.location ?? null) : null,
        mkConversationId,
    });
    useStopKey(drafting, applyTrigger);

    // The toast's copy + tone and the export formats are pure projections
    // (own hooks); noticeDeps is what a notice's actions run (useNoticeView).
    const noticeDeps: NoticeViewDeps = {
        undoDelete,
        undoDeleteConversation,
        unarchiveConversationNow,
        retrySave,
        deleteResourceNow,
        removeFromModule: studio.removeFromModule,
        movePartBack: studio.movePartBack,
        putPartBack: studio.putPartBack,
        putBackOnCanvas: studio.putBackOnCanvas,
        restoreProject: (id) => restoreProjectNow(ports, setLibrary, id),
        sendPrompt: submitText,
        turnFailureText,
        showUploadedSources: gotoKnowledge,
        startTour: coach.startTour,
        keepMine: editor.keepMine,
        takeTheirs: editor.takeTheirs,
    };

    const exportFormats = useExportFormats(activeMaterialTemplate);

    const { t } = useLingui();
    const untitledTitle = t`Untitled`;
    const activeMaterialTitle = library?.materials.find(
        (m) => m.id === activeMaterialId,
    )?.title;
    const activeTitle =
        activeMaterialTitle !== undefined
            ? resourceDisplayTitle(
                  activeMaterialTitle,
                  "material",
                  untitledTitle,
              )
            : "";

    // The learning contexts as pickable options — fed to the ContextSideCard's
    // situation section ("who this is for").
    const selectableContexts: readonly SelectableContext[] =
        learningContexts.map((lc) => ({ id: lc.id, title: lc.title }));

    // The wall clock, ticked via the sanctioned `useNow` seam and threaded down
    // as a plain number, so every render (Home's
    // freshness, the search projection) stays Date-free.
    const nowMs = useNow(30_000);
    // F63 — neutral title for a titleless (no-first-message) thread; read once
    // here (stable per locale) and threaded into the pure search projection.
    const conversationFallbackTitle = t`New conversation`;

    // The Search tab's entity pool — the ONE pure projection over the live
    // Library + conversation summaries, freshness stamped against `nowMs`
    // (searchProjection.ts). Recomputed on the 30s tick so relative labels stay
    // fresh; the pure SearchCentre ranks/filters it client-side per keystroke.
    const searchResults = useMemo(
        () =>
            libraryToSearchResults(
                library,
                conversationSummaries,
                nowMs,
                conversationFallbackTitle,
                untitledTitle,
                (first) => t`${first} et al.`,
            ),
        [
            library,
            conversationSummaries,
            nowMs,
            conversationFallbackTitle,
            untitledTitle,
            t,
        ],
    );

    // F83 linkage recorder: stamp each just-queued IngestId with the drop-time
    // conversation so the pure `composerAttachmentsFor` projection knows whose
    // composer the optimistic chip belongs to. A drop with no active
    // conversation records nothing (rail-only). Total, fresh-value transition —
    // the only writer of this VIEW-TRANSIENT map.
    const recordIngestConversation = (
        ids: readonly IngestId[],
        conversationId: string | null,
    ): void => {
        if (conversationId === null || ids.length === 0) return;
        setIngestConversations((prev) => {
            const next = new Map(prev);
            for (const id of ids) next.set(id, conversationId);
            return next;
        });
    };

    const { heroLayoutFor } = useHeroHost({
        isFreshForAuthor: coach.isFreshForAuthor,
        conversationSummaries,
        pendingGrounding,
        composerMentions,
        selectableContexts,
        composerCandidates,
        chatSlashCommands,
        displayName,
        turnFailureText,
        tipsDismissed,
        setTipsDismissed,
        tipIndex,
        sessionFor,
        refFor,
        toComposerResourceRef,
        openFilePicker,
        sendChat,
        submitText,
        retryTurn,
        stopTurn,
        openConversation,
        selectCategory: navHost.selectCategory,
        dispatch,
        arcProminence: coach.arcProminence,
        requestGuidedTour: coach.viewTour?.start ?? null,
        // F83: the transient sources the hero composer's optimistic chips fold
        // from — the ingest batch and its drop-time conversation linkage.
        ingestBatch: ingest,
        ingestConversations,
    });

    // COLD-OPEN: cold-open rests on the CALM EMPTY LANDING (home / WorkspaceHome).
    // The resting state lives in the pure `emptyTabs`, not here.

    // Load one resource's version history into the drill-down when the surface
    // asks for it (a timeline row expand). Tracks the resourceKey so a later
    // restore knows its subject.
    const onRequestFileHistory = (resourceKey: string) => {
        setHistoryVersionId(null);
        const run = async () => {
            const r = await ports.loadFileHistory(resourceKey);
            setFileHistory(
                Result.isOk(r) ? { resourceKey, view: r.value } : null,
            );
        };
        void run();
    };

    // Restore a resource to an earlier version — dispatches the version-restore
    // port (a typed refusal today; see the ports gap). A failure surfaces as a
    // lifecycle notice; a success would reload the drilled history.
    const onRestoreVersion = (resourceKey: string, marker: string) => {
        const run = async () => {
            const r = await ports.restoreResourceVersion(resourceKey, marker);
            if (Result.isErr(r)) {
                showNotice({
                    type: "lifecycle-failed",
                    message: displayText(r.error),
                });
                return;
            }
            onRequestFileHistory(resourceKey);
        };
        void run();
    };

    // Open the History surface drilled to a specific resource (the resource
    // overflow's "Version history" item). Opens the overlay, then requests that
    // resource's file-history so the drill-down lands ready.
    const openHistoryForResource = (resourceKey: string) => {
        openOverlay({ type: "history" });
        onRequestFileHistory(resourceKey);
    };

    // The right zone (SecondaryPane) is a pure projection now — SecondaryDock
    // reads the model below. Its scrim-close intent stays here (the frame owns
    // it): closing collapses the right zone (the dock slot only).
    const closeSecondary = () => dispatch({ type: "close-secondary" });

    // The typed bundle the render tree projects (ui-as-projection): assembled
    // once from the shell's state/derivations, provided below so every view
    // component reads facts + emits intents off it instead of closing over
    // these locals. Grows as render helpers move into view components.
    const model: AppShellModel = {
        routeNotFound,
        onRouteHome,
        frame,
        dispatch,
        ports,
        editor,
        navHost,
        coach,
        materialSide,
        openRef,
        onActivateTab,
        onFinderJump,
        goToChange,
        onCloseTab,
        openConversation,
        enterFullChat,
        applyIngest,
        introduceIntoActiveContext,
        dropFromActiveContext,
        clearActiveContext,
        promoteAmbientItem,
        handleMentionsChange,
        groundAddedSource,
        sendChat,
        submitText,
        retryTurn,
        stopTurn,
        acceptOpeningMove,
        draftMaterialFromSource,
        supplyFormAnswer,
        health,
        lastCheckedAt,
        healthReason,
        nextRetryAt,
        retryNow,
        setRetrySeq,
        signOut,
        conversationTitleFor,
        submitResourceRename,
        submitConversationRename,
        deleteResourceNow,
        undoDelete,
        archiveResourceNow,
        unarchiveResourceNow,
        promoteFileNow,
        deleteConversationNow,
        undoDeleteConversation,
        saveSourceSummaryNow,
        addSourceNote,
        deleteSourceNote,
        requestUploadForSource,
        downloadExportById,
        resourceOverflowItems,
        beginRenameConversation,
        archiveConversationNow,
        unarchiveConversationNow,
        retrySave,
        packageNow,
        onExportStart,
        heroLayoutFor,
        library,
        libraryError,
        learningContexts,
        authStatus,
        sourceDetail,
        sourceError,
        exportDetail,
        exportError,
        quality,
        policy,
        policyCache,
        conversationSummaries,
        conversationList,
        retryConversationList,
        searchResults,
        libraryMount,
        activeRef,
        activeConversationId,
        activeMaterialId,
        activeTitle,
        isSplit,
        tabs,
        dock,
        surface,
        scope,
        paneSubject,
        refFor,
        chatState,
        sessionFor,
        conversationTranscriptPending,
        composerCandidates,
        composerMentions,
        chatSlashCommands,
        selectableContexts,
        activeContextItems,
        activeAmbientItems,
        pendingGrounding,
        sourceItems,
        exportFormats,
        exportState,
        setExportState,
        exportReady,
        setExportReady,
        templateCatalog,
        createState,
        createSeedTitle,
        onCreateMaterial,
        historyView,
        fileHistory,
        historyVersionId,
        setHistoryVersionId,
        onRequestFileHistory,
        onRestoreVersion,
        finderDocs,
        finderLoading,
        renaming,
        setRenaming,
        confirmDelete,
        setConfirmDelete,
        confirmDeleteConversation,
        setConfirmDeleteConversation,
        ingest,
        ingestHostRef,
        setIngest,
        ingestConversations,
        recordIngestConversation,
        setLibrary,
        panelLayout,
        dockDragPull,
        setDockDragPull,
        railDragPull,
        setRailDragPull,
        dockDragCfg,
        railDragCfg,
        resizeRail,
        resetRail,
        resizeDock,
        resetDock,
        endDockDrag,
        endRailDrag,
        revealRail,
        closeSecondary,
        displayName,
        onDisplayNameChange,
        workspaceName,
        onWorkspaceNameChange,
        summaryConsent,
        onSummaryConsentChange,
        kbdPlatform,
        nowMs,
        showNotice,
        messageControls,
        openOverlay,
        closeOverlay,
        summonSlot,
        gotoSettings,
        closeSettings,
        gotoHome,
        closeHome,
        gotoStudio,
        studio,
        gotoKnowledge,
        gotoSearch,
        summonSearch,
        closeSearch,
        navBack,
        navForward,
        mkConversationId,
        startNewConversation,
        openCreateDialog: () => openCreateDialog(),
        openFilePicker,
        addPastedText,
        displayText,
        turnFailureText,
        noticeDeps,
    };

    return model;
};
